/**
 * TypeScript wrapper for the WASM inking module.
 *
 * Mirrors the public API of the original TypeScript inking pipeline so
 * callers (and tests) can drop WASM in without behavioural changes.
 *
 * The Rust side is stateless and takes flat typed-array slices. This module
 * owns the pack/unpack glue between rich TS interfaces and the WASM FFI.
 */

import type {
  FeatureMap,
  FeatureType,
  GBufferData,
  InkingPipelineParams,
  InkingPipelineResult,
  StrokeCurve,
  StyledStroke,
} from "../viewport/inking/cpu/types.ts";
import {
  inking_adaptive_smooth_polyline as wasmAdaptiveSmoothPolyline,
  inking_compute_deep_shadow_mask as wasmComputeDeepShadowMask,
  inking_compute_opacity as wasmComputeOpacity,
  inking_compute_thickness as wasmComputeThickness,
  inking_detect_corner_mask as wasmDetectCornerMask,
  inking_extract_creases as wasmExtractCreases,
  inking_extract_screen_edges as wasmExtractScreenEdges,
  inking_extract_silhouettes as wasmExtractSilhouettes,
  inking_get_stroke_smoothing_profile as wasmGetStrokeSmoothingProfile,
  inking_is_same_object_silhouette_depth_compatible as wasmIsSameObjectSilhouetteDepthCompatible,
  inking_run_pipeline as wasmRunPipeline,
  inking_sample_scalar_sobel as wasmSampleScalarSobel,
  inking_sample_scalar_sobel_same_surface as wasmSampleScalarSobelSameSurface,
  inking_sample_vec3_sobel as wasmSampleVec3Sobel,
  inking_sample_vec3_sobel_same_surface as wasmSampleVec3SobelSameSurface,
  inking_smooth_polyline as wasmSmoothPolyline,
  inking_subsample_polyline as wasmSubsamplePolyline,
  inking_thin_feature_ridges as wasmThinFeatureRidges,
} from "./pkg/poseref_wasm.js";

// ---------------------------------------------------------------------------
// Parameter packing — exact stride must match INKING_PARAMS_STRIDE in types.rs
// ---------------------------------------------------------------------------

const PARAMS_STRIDE = 21;

function packParams(params: InkingPipelineParams): Float32Array {
  const out = new Float32Array(PARAMS_STRIDE);
  out[0] = params.features.silhouette ? 1 : 0;
  out[1] = params.features.crease ? 1 : 0;
  out[2] = params.features.screenEdges ? 1 : 0;
  out[3] = params.creaseAngle;
  out[4] = params.screenEdgeDetail;
  out[5] = params.simplificationTolerance;
  out[6] = params.cornerAngle;
  out[7] = params.minChainLength;
  out[8] = params.baseThickness;
  out[9] = params.thicknessMin;
  out[10] = params.shadowResponse;
  out[11] = params.shadowExposure;
  out[12] = params.silhouetteBoost;
  out[13] = params.distanceNear;
  out[14] = params.distanceFar;
  out[15] = params.taperStrength;
  out[16] = params.baseOpacity;
  out[17] = params.lightRangeMin;
  out[18] = params.lightRangeMax;
  out[19] = params.cameraNear;
  out[20] = params.cameraFar;
  return out;
}

// ---------------------------------------------------------------------------
// Feature detectors — mutate `out` in place to match TS semantics
// ---------------------------------------------------------------------------

export function extractSilhouettes(
  gbuffer: GBufferData,
  out: FeatureMap,
): void {
  wasmExtractSilhouettes(
    gbuffer.width,
    gbuffer.height,
    gbuffer.depth,
    gbuffer.objectIds,
    gbuffer.normals,
    out.confidence,
    out.featureType,
    out.silhouetteObjectId,
    out.silhouetteNeighborId,
    out.silhouetteKind,
  );
}

export function extractCreases(
  gbuffer: GBufferData,
  creaseAngle: number,
  out: FeatureMap,
): void {
  wasmExtractCreases(
    gbuffer.width,
    gbuffer.height,
    gbuffer.depth,
    gbuffer.normals,
    gbuffer.objectIds,
    creaseAngle,
    out.confidence,
    out.featureType,
    out.silhouetteObjectId,
    out.silhouetteNeighborId,
    out.silhouetteKind,
  );
}

export function extractScreenEdges(
  gbuffer: GBufferData,
  detail: number,
  _deepShadowMask: Float32Array,
  out: FeatureMap,
): void {
  // The TS screen-edge detector never reads `_deepShadowMask` directly — it
  // was plumbed through for a suppression path that ended up not firing
  // (see the TS file). Keep the parameter for signature compatibility.
  wasmExtractScreenEdges(
    gbuffer.width,
    gbuffer.height,
    gbuffer.depth,
    gbuffer.normals,
    gbuffer.objectIds,
    detail,
    out.confidence,
    out.featureType,
    out.silhouetteObjectId,
    out.silhouetteNeighborId,
    out.silhouetteKind,
  );
}

export function thinFeatureRidges(
  featureMap: FeatureMap,
  minConfidence: number = 0.15,
): FeatureMap {
  const next = new Float32Array(featureMap.confidence);
  wasmThinFeatureRidges(
    featureMap.width,
    featureMap.height,
    next,
    featureMap.featureType,
    minConfidence,
  );
  return {
    width: featureMap.width,
    height: featureMap.height,
    confidence: next,
    featureType: featureMap.featureType,
    silhouetteObjectId: featureMap.silhouetteObjectId,
    silhouetteNeighborId: featureMap.silhouetteNeighborId,
    silhouetteKind: featureMap.silhouetteKind,
    resolvedAutoSmoothAngles: featureMap.resolvedAutoSmoothAngles,
  };
}

// ---------------------------------------------------------------------------
// Signal utilities
// ---------------------------------------------------------------------------

export function sampleScalarSobel(
  values: Float32Array,
  width: number,
  x: number,
  y: number,
): { gx: number; gy: number; magnitude: number } {
  const r = wasmSampleScalarSobel(values, width, x, y);
  return { gx: r[0], gy: r[1], magnitude: r[2] };
}

export function sampleScalarSobelSameSurface(
  values: Float32Array,
  depth: Float32Array,
  objectIds: Uint32Array,
  width: number,
  x: number,
  y: number,
): { gx: number; gy: number; magnitude: number } {
  const r = wasmSampleScalarSobelSameSurface(
    values,
    depth,
    objectIds,
    width,
    x,
    y,
  );
  return { gx: r[0], gy: r[1], magnitude: r[2] };
}

export function sampleVec3Sobel(
  values: Float32Array,
  width: number,
  x: number,
  y: number,
): { magnitude: number } {
  return { magnitude: wasmSampleVec3Sobel(values, width, x, y) };
}

export function sampleVec3SobelSameSurface(
  values: Float32Array,
  depth: Float32Array,
  objectIds: Uint32Array,
  width: number,
  x: number,
  y: number,
): { magnitude: number } {
  return {
    magnitude: wasmSampleVec3SobelSameSurface(
      values,
      depth,
      objectIds,
      width,
      x,
      y,
    ),
  };
}

export function computeDeepShadowMask(
  gbuffer: GBufferData,
  params: InkingPipelineParams,
): Float32Array {
  return wasmComputeDeepShadowMask(
    gbuffer.width,
    gbuffer.height,
    gbuffer.depth,
    gbuffer.luminance,
    packParams(params),
  );
}

// ---------------------------------------------------------------------------
// Smoothing utilities
// ---------------------------------------------------------------------------

export interface StrokeSmoothingProfile {
  sigmaMin: number;
  sigmaMax: number;
  curvatureThreshold: number;
  cornerAngleDeg: number;
  thicknessSmoothingRadius: number;
  boundarySmoothingRadius: number;
  cornerProtectionRadius: number;
}

/**
 * Resolve the smoothing profile for a chain.
 *
 * `bboxDiagPx` is the bounding-box diagonal of the chain's polyline in
 * pixels and drives the scale-aware σ ramp: small chains (≤ 20 px diagonal)
 * get a tighter σ range so fine features survive smoothing, while larger
 * chains keep the full hand-drawn σ range. Helpers like `polylineBboxDiag`
 * compute this from a sample buffer.
 */
export function getStrokeSmoothingProfile(
  featureType: FeatureType,
  resolvedAutoSmoothAngle: number,
  bboxDiagPx: number,
): StrokeSmoothingProfile {
  const r = wasmGetStrokeSmoothingProfile(
    featureType,
    resolvedAutoSmoothAngle,
    bboxDiagPx,
  );
  return {
    sigmaMin: r[0],
    sigmaMax: r[1],
    curvatureThreshold: r[2],
    cornerAngleDeg: r[3],
    thicknessSmoothingRadius: r[4],
    boundarySmoothingRadius: r[5],
    cornerProtectionRadius: r[6],
  };
}

/**
 * Bounding-box diagonal of an interleaved `[x0, y0, x1, y1, …]` polyline,
 * in pixels. Returns 0 for empty input.
 */
export function polylineBboxDiag(points: Float32Array): number {
  const n = points.length / 2;
  if (n === 0) return 0;
  let minX = points[0];
  let maxX = points[0];
  let minY = points[1];
  let maxY = points[1];
  for (let i = 1; i < n; i++) {
    const x = points[i * 2];
    const y = points[i * 2 + 1];
    if (x < minX) minX = x;
    else if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    else if (y > maxY) maxY = y;
  }
  const dx = maxX - minX;
  const dy = maxY - minY;
  return Math.sqrt(dx * dx + dy * dy);
}

export function detectCornerMask(
  points: Float32Array,
  cornerAngleDeg: number,
  closed: boolean = false,
): Uint8Array {
  return wasmDetectCornerMask(points, cornerAngleDeg, closed);
}

export function smoothPolyline(
  points: Float32Array,
  sigma: number,
  closed: boolean = false,
): Float32Array {
  return wasmSmoothPolyline(points, sigma, closed);
}

export function adaptiveSmoothPolyline(
  points: Float32Array,
  sigmaMin: number,
  sigmaMax: number,
  curvatureThreshold: number,
  closed: boolean = false,
  cornerAngleDeg: number = 70,
): Float32Array {
  return wasmAdaptiveSmoothPolyline(
    points,
    sigmaMin,
    sigmaMax,
    curvatureThreshold,
    closed,
    cornerAngleDeg,
  );
}

export function subsamplePolyline(
  points: Float32Array,
  interval: number,
  closed: boolean = false,
  cornerAngleDeg: number = 70,
): Float32Array {
  return wasmSubsamplePolyline(points, interval, closed, cornerAngleDeg);
}

// ---------------------------------------------------------------------------
// Styling
// ---------------------------------------------------------------------------

export function computeThickness(
  samples: Float32Array,
  featureType: FeatureType,
  gbuffer: GBufferData,
  params: InkingPipelineParams,
  closed: boolean = false,
): Float32Array {
  return wasmComputeThickness(
    samples,
    featureType,
    gbuffer.width,
    gbuffer.height,
    gbuffer.depth,
    gbuffer.luminance,
    packParams(params),
    closed,
  );
}

export function computeOpacity(
  samples: Float32Array,
  featureType: FeatureType,
  confidence: number,
  gbuffer: GBufferData,
  params: InkingPipelineParams,
): Float32Array {
  return wasmComputeOpacity(
    samples,
    featureType,
    confidence,
    gbuffer.width,
    gbuffer.height,
    gbuffer.depth,
    gbuffer.luminance,
    packParams(params),
  );
}

// ---------------------------------------------------------------------------
// Pipeline predicates
// ---------------------------------------------------------------------------

export function isSameObjectSilhouetteDepthCompatible(
  featureMap: FeatureMap,
  depth: Float32Array,
  a: number,
  b: number,
): boolean {
  return wasmIsSameObjectSilhouetteDepthCompatible(
    featureMap.width,
    featureMap.height,
    featureMap.silhouetteObjectId,
    featureMap.silhouetteNeighborId,
    depth,
    a,
    b,
  );
}

// ---------------------------------------------------------------------------
// End-to-end pipeline
// ---------------------------------------------------------------------------

const STROKE_META_STRIDE = 7;

/**
 * Run the full CPU inking pipeline in WASM and unpack the result into the
 * `InkingPipelineResult` shape expected by TS callers.
 *
 * The returned `elapsedMs` is measured on the JS side so it reflects wall-
 * clock latency including the WASM call overhead.
 */
export function runInkingPipeline(
  gbuffer: GBufferData,
  params: InkingPipelineParams,
): InkingPipelineResult {
  const t0 = performance.now();

  const result = wasmRunPipeline(
    gbuffer.width,
    gbuffer.height,
    gbuffer.depth,
    gbuffer.normals,
    gbuffer.objectIds,
    gbuffer.luminance,
    gbuffer.resolvedAutoSmoothAngles,
    packParams(params),
  );

  const samples = result.samples;
  const thickness = result.thickness;
  const opacity = result.opacity;
  const offsets = result.stroke_offsets;
  const meta = result.stroke_meta;
  const strokeCount = result.stroke_count;
  const featurePixels = result.feature_pixels;
  const chainCount = result.chain_count;
  const curveCount = result.curve_count;
  result.free();

  const strokes: StyledStroke[] = new Array(strokeCount);
  for (let k = 0; k < strokeCount; k++) {
    const start = offsets[k];
    const end = offsets[k + 1];
    const sampleCount = end - start;

    const strokeSamples = samples.slice(start * 2, end * 2);
    const strokeThickness = thickness.slice(start, end);
    const strokeOpacity = opacity.slice(start, end);

    const m = k * STROKE_META_STRIDE;
    const featureType = meta[m] as FeatureType;
    const confidence = meta[m + 1];
    const featureMix: [number, number, number] = [
      meta[m + 2],
      meta[m + 3],
      meta[m + 4],
    ];
    const resolvedAutoSmoothAngle = meta[m + 5];
    const closed = meta[m + 6] !== 0;

    // `curve.segments` is preserved as an empty array — the polygon renderer
    // uses `samples` + `style` directly. The `curve` envelope is kept so
    // downstream SVG export paths still compile.
    const curve: StrokeCurve = {
      segments: [],
      featureType,
      confidence,
      featureMix,
      closed,
      resolvedAutoSmoothAngle,
    };

    strokes[k] = {
      curve,
      style: { thickness: strokeThickness, opacity: strokeOpacity },
      samples: strokeSamples,
    };

    void sampleCount;
  }

  const elapsedMs = performance.now() - t0;

  return {
    strokes,
    stats: {
      featurePixels,
      chainCount,
      curveCount,
      totalSegments: curveCount,
      elapsedMs,
    },
  };
}
